import * as React from 'react';
import * as ReactMarkdown from 'react-markdown';
import 'intersection-observer';
import { Entry } from 'contentful';

import styled, { tstyled } from '../../../theme';
import { IProjectContent } from '../../../models/Project';
import requestIdleCallback from '../../../requestIdleCallback';

const TextContainer = tstyled<{ visible: boolean }>(styled.div)`
  color: ${(props) => props.theme.color.body2};
  position: relative;
  transform: ${(props) => (props.visible ? 'none' : 'translateX(-30px)')};
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: 0.65s ease transform 0.35s, 0.65s ease-out opacity 0.35s;

  @media (min-width: 800px) {
    width: 740px;
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: ${(props) => props.theme.mobileBreakpoint - 1}px) {
    padding: ${(props) => props.theme.mobilePadding}px;
    padding-bottom: 0;
  }

  @media (min-width: ${(props) => props.theme.mobileBreakpoint}px) {
    display: flex;
    justify-content: space-between;
    width: auto;
  }
`;

const TextHeader = styled.h3`
  font-size: 20px;
  flex-grow: 0;
  flex-shrink: 0;
  line-height: 1.2;
  font-family: 'rather';
  font-weight: 200;

  @media (max-width: ${(props) => props.theme.mobileBreakpoint - 1}px) {
    margin-bottom: ${(props) => props.theme.mobilePadding}px;
  }

  @media (min-width: ${(props) => props.theme.mobileBreakpoint}px) {
    margin-right: 40px;
    flex-basis: 35%;
  }

  @media (min-width: 1800px) {
    font-size: 1.6vw;
  }
`;

const TextDescription = styled.div`
  flex-shrink: 1;
  flex-grow: 0;
  line-height: 1.5;

  @media (min-width: 1800px) {
    font-size: 0.8vw;
  }

  p {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    display: inline-block;
    color: ${(props) => props.theme.color.body2};

    &:hover {
      transform: scaleY(-1);
    }
  }
`;

const ReadMore = styled.div`
  text-transform: uppercase;
  color: ${(props) => props.theme.color.body2};
  left: 0;
  font-size: 12px;
  cursor: pointer;
  top: 100%;
  padding-top: 5px;

  &:hover {
    transform: scaleY(-1);
  }
`;

const max = 280;
const linkRegex =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/;
class TextDescriptionRestrictedContent extends React.PureComponent<
  {
    description: string;
  },
  { expanded: boolean }
> {
  state: { expanded: boolean } = { expanded: false };

  onClickReadMore = () => {
    this.setState({ expanded: true });
  };

  getRealLength(description: string) {
    return this.chunkLength(this.splitDescription(description));
  }

  splitDescription(description: string) {
    return description
      .split(/\s/)
      .filter((s) => s.trim() !== '' && !linkRegex.test(s));
  }

  chunkLength(s: string[]) {
    return s.reduce((sum, x) => sum + x.length, 0);
  }

  trim = () => {
    const pieces = this.splitDescription(this.props.description);
    const totalLength = this.chunkLength(pieces);

    if (totalLength < max) {
      return this.props.description;
    }

    const toShow: string[] = [];
    let count = 0;
    let i = 0;

    while (count < max && i < pieces.length) {
      const nextChunk = pieces[i++];
      toShow.push(nextChunk);
      count += nextChunk.length + 1;
    }

    return `${toShow.join(' ')}...`;
  };

  render() {
    const toShow = this.state.expanded ? this.props.description : this.trim();
    return (
      <TextDescription>
        <div style={{ position: 'relative' }}>
          <ReactMarkdown
            source={toShow}
            renderers={{
              link: (p) => {
                if (!p.href.startsWith('http')) {
                  return p.href;
                }

                return (
                  <a
                    href={p.href}
                    rel="nofollow noreferrer noopener"
                    target="_blank"
                  >
                    {p.children}
                  </a>
                );
              },
            }}
          />
          {this.getRealLength(this.props.description) >= max &&
            !this.state.expanded && (
              <ReadMore onClick={this.onClickReadMore}>Read More</ReadMore>
            )}
        </div>
      </TextDescription>
    );
  }
}

interface IProps {
  projectContent: Entry<IProjectContent>;
  containerWidth: number;
}
interface IState {
  visible: boolean;
}
export default class Text extends React.PureComponent<IProps, IState> {
  state = {
    visible: false,
  };

  intersectionObserver?: IntersectionObserver;
  container: HTMLDivElement;

  componentDidMount() {
    requestIdleCallback(this.createIntersectionObserver);
  }

  componentWillUnmount() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  createIntersectionObserver = () => {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      requestIdleCallback(() => {
        if (entries[0].isIntersecting) {
          this.setState({ visible: true });
          if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
          }
        }
      });
    });
    this.intersectionObserver.observe(this.container);
  };

  render() {
    const { projectContent } = this.props;

    return (
      <TextContainer
        visible={this.state.visible}
        innerRef={(ref) => (this.container = ref)}
      >
        <TextHeader>{projectContent.fields.title}</TextHeader>
        <TextDescriptionRestrictedContent
          description={projectContent.fields.description}
        />
      </TextContainer>
    );
  }
}



// WEBPACK FOOTER //
// ./src/scenes/Project/ProjectContent/Text.tsx